import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiChecklistHelperService, IChecklistCheckpointPlaceholderLabelData } from '../../fi-checklist-helper.service';
import { ISectionCardData } from '@fi-shared/fi-section-card/fi-section-card';
import { Subject } from 'rxjs';
import { FiChecklistSectionTypes } from '@fi-shared/checklist';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import { IChecklistV2, ICheckpointGroup } from '@fi-shared/checklist/checklist-v2';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgModel, ValidationErrors } from '@angular/forms';
import { ContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/content-language-label.model';
import { takeUntil } from 'rxjs/operators';
import { IFiObjectEditorInfo } from '@fi-shared/fi-object-editor-info/fi-object-editor-info';
import { cloneDeep, getObjectTypeNameTextId, isDefined, isEqual } from '@fi-shared/utils';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import {
    IAccessRightCoreFilter,
    IAccessRightFilterCollection,
    IAccessRightSelectionFiltersCollection,
    ILightWeightArea,
} from '@fi-shared/models';
import { mergeLightWeightObjects } from '@fi-shared/utils/merge-light-weight-objects';
import { fiItemsFlatten } from '@fi-shared/utils/fi-items-flatten';
import { IFiDialogOptions } from '@fi-shared/fi-dialogs/fi-dialogs';
import {
    FiChecklist2QRCodesDialogComponent,
    IFiChecklist2QRCodesDialogData,
    IFiChecklist2QRCodesDialogResult,
} from '@fi-shared/checklist/fi-checklist2-edit/general-section-edit/qr-codes-dialog/qr-codes-dialog.component';
import { FiAccessRightHelperService } from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { ModuleType, ObjectTypes } from '@fi-shared/types';
import { FiToggleSwitchLabelAlign } from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import {
    FiComplianceThresholdDialogComponent,
    IComplianceThresholdDialogData,
    IComplianceThresholdDialogResult,
} from '@fi-shared/checklist/fi-checklist2-edit/general-section-edit/compliance-threshold-dialog/compliance-threshold-dialog.component';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { IAIChecklist2Model } from '@fi-shared/fi-ai-assistance/fi-ai-assistance';
import { FiItemSharingSettingsDialogService } from '@fi-shared/access-right-filters/fi-item-sharing-settings-dialog/fi-item-sharing-settings.service';

@Component({
    selector: 'fi-checklist2-general-section-edit',
    templateUrl: './general-section-edit.component.html',
    styleUrls: ['./general-section-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-general-section-edit' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiChecklist2GeneralSectionEditComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiChecklist2GeneralSectionEditComponent,
        },
    ],
    standalone: false,
})
export class FiChecklist2GeneralSectionEditComponent
    extends ControlValueAccessorValidatorBase<IChecklistV2>
    implements OnInit, OnDestroy
{
    @Input() accessRightFilterCollection: IAccessRightFilterCollection;
    @Input() templateMode: boolean;
    @Input() isTemplateBased: boolean;
    @Input({ required: true }) auditMode: boolean;
    @Input() savedQuickSelectionFilters: IAccessRightSelectionFiltersCollection[] = [];

    @Input() set currentSection(value: FiChecklistSectionTypes) {
        this.setSettingsTooltipHiddenStatus(value !== FiChecklistSectionTypes.General);
    }

    @Output() navigateToCheckpointSections = new EventEmitter<void>();
    @Output() saveClick = new EventEmitter<void>();

    protected readonly ObjectTypes = ObjectTypes;

    protected availableAccessRightFilterCollection: IAccessRightFilterCollection;
    protected businessUnitIdsOutOfScope: number[] = [];
    protected availableSections: ISectionCardData[] = [];

    protected get checklist(): IChecklistV2 {
        return this.ngModel;
    }

    protected get selectedLanguageId(): number {
        return this.fiChecklistStoreService.selectedLanguageId;
    }

    protected labelData: IChecklistCheckpointPlaceholderLabelData;

    constructor(
        injector: Injector,
        private fiChecklistHelperService: FiChecklistHelperService,
        private fiChecklistStoreService: FiChecklistStoreService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private detectDevicesService: DetectDevicesService,
        private fiAccessRightHelperService: FiAccessRightHelperService,
        private fiUserDataService: FiUserDataService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fiItemSharingSettingsDialogService: FiItemSharingSettingsDialogService,
    ) {
        super(injector);
    }

    public ngOnInit(): void {
        this.setAvailableAccessRights();
        this.setAvailableSections();
        this.subscribeOnChecklistPropertiesChanged();
        this.fiChecklistHelperService.setSelectedLanguageByNames(this.checklist.Names);
        this.labelData = this.templateMode
            ? this.fiChecklistHelperService.checklistTemplatePlaceholderLabelData
            : this.fiChecklistHelperService.checklistPlaceholderLabelData;

        /*if (this.isAIAssistanceEnabled()) {
            this.openAIAssistanceDialog();
        }*/
    }

    protected isAIAssistanceEnabled(): boolean {
        return this.fiChecklistHelperService.isAIAssistanceEnabled();
    }

    protected getPageHeader(): string {
        return this.templateMode ? this.getChecklistTemplateHeader() : this.getChecklistHeader();
    }

    private getChecklistHeader(): string {
        if (this.checklist.Id) {
            const textId = getObjectTypeNameTextId(
                !this.auditMode ? ObjectTypes.Checklist2 : ObjectTypes.Checklist2Audit,
            );
            const editText = this.appTextService.get('checklist.edit_checklist_v2', {
                '{0}': this.appTextService.get(textId).toLowerCase(),
            });
            return `${editText}: ${this.checklist.Name}`;
        } else {
            if (!this.auditMode) return this.appTextService.get('checklist.new_checklist');
            else return this.appTextService.get('checklist.new_audit_checklist');
        }
    }

    private getChecklistTemplateHeader(): string {
        return this.checklist.Id
            ? `${this.appTextService.get('checklist.edit_template')}: ${this.checklist.Name}`
            : this.appTextService.get('common.new_template');
    }

    protected onBackButtonClick(): void {
        window.history.back();
    }

    private setAvailableAccessRights(): void {
        this.availableAccessRightFilterCollection = cloneDeep(this.accessRightFilterCollection);

        const mergeResult = mergeLightWeightObjects(
            this.accessRightFilterCollection.AccessRightCoreFilter.BusinessUnits,
            this.checklist.AccessRightFilterCollection.AccessRightCoreFilter.BusinessUnits,
        );
        const availableBusinessUnits = mergeResult.mergedResult;

        this.businessUnitIdsOutOfScope = mergeResult.disabledItemIds;
        this.availableAccessRightFilterCollection.AccessRightCoreFilter.BusinessUnits = availableBusinessUnits;

        this.initChecklistDefaultSharingSettings();
    }

    private selectedFiltersCollection: IAccessRightSelectionFiltersCollection;
    private setSelectedFiltersCollection(): void {
        this.selectedFiltersCollection = this.fiItemSharingSettingsDialogService.getSavedFilterBySharingSelection(
            this.checklist.AccessRightFilterCollection.AccessRightCoreFilter,
            this.savedQuickSelectionFilters,
        );
    }
    private initChecklistDefaultSharingSettings(): void {
        this.setSelectedFiltersCollection();

        if (isDefined(this.selectedFiltersCollection)) {
            return;
        }

        // Set as default feature is disabled https://chainformation.atlassian.net/browse/FI-6357?focusedCommentId=62247
        /*if (!this.checklist.Id) {
            const defaultFilterSelection = this.savedQuickSelectionFilters
                .find(({ IsDefault }) => IsDefault);

            if (isDefined(defaultFilterSelection)) {
                this.selectedFiltersCollection = defaultFilterSelection;
                this.checklist.AccessRightFilterCollection.AccessRightCoreFilter = cloneDeep(defaultFilterSelection.SelectionFilterSettingCollection.AccessRightCoreFilter);
            }
        }*/
    }

    private subscribeOnChecklistPropertiesChanged(): void {
        this.fiChecklistStoreService
            .onChecklistDataChange$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.doPropagateChanges();
            });
    }

    protected isThreeColumnTemplate(): boolean {
        return this.isTranslationCardVisible() || this.isAIAssistanceEnabled();
    }

    protected isTranslationCardVisible(): boolean {
        return this.fiChecklistHelperService.isTranslationCardVisible();
    }

    protected isUserInformationShown(): boolean {
        const checklistCreatorEditorData = this.getChecklistEditorData();

        return (
            isDefined(checklistCreatorEditorData.ChangedByProfile) ||
            isDefined(checklistCreatorEditorData.CreatedByProfile)
        );
    }

    private setAvailableSections(): void {
        // note: removing general since general section is FiChecklist2GeneralSectionEditComponent
        this.availableSections = this.fiChecklistHelperService
            .getAvailableSections()
            .filter((section: ISectionCardData) => section.id !== FiChecklistSectionTypes.General)
            .map((section: ISectionCardData) => {
                if (section.id === FiChecklistSectionTypes.ComplianceThreshold) {
                    section.onClick = () => this.openComplianceThresholdDialog();
                } else if (section.id === FiChecklistSectionTypes.Settings) {
                    section.onClick = () => this.openSettingsDialog();
                } else if (section.id === FiChecklistSectionTypes.QRCodes) {
                    section.onClick = () => this.openQRCodesDialog();
                } else if (section.id === FiChecklistSectionTypes.Checkpoints) {
                    section.onClick = () => this.navigateToCheckpointSections.emit();
                }
                return section;
            });
    }

    private readonly commonDialogOptions: IFiDialogOptions<any, any> = {
        yesButtonText: this.appTextService.get('done'),
        hideDialogTopBorder: true,
        isHeaderDividerShown: true,
        isFooterDividerShown: true,
        preventUppercaseButtonLabel: true,
        config: {
            panelClass: ['fi-dialog-with-dividers', 'fi-rounded-dialog-panel', 'fi-rounded-dialog-full-round-buttons'],
            autoFocus: false,
            width: this.detectDevicesService.isMobilePhone() ? '80%' : '50%',
        },
    };

    private openComplianceThresholdDialog(): void {
        this.setSettingsTooltipHiddenStatus(true);
        const _commonDialogOptions = cloneDeep(this.commonDialogOptions);
        const dialogOptions: IFiDialogOptions<IComplianceThresholdDialogData, IComplianceThresholdDialogResult> =
            Object.assign(_commonDialogOptions, {
                contentComponentRef: FiComplianceThresholdDialogComponent,
                contentComponentData: {
                    isComplianceThresholdEnabled: this.checklist.IsComplianceThresholdEnabled,
                    complianceThreshold: this.checklist.ComplianceThreshold,
                    complianceThresholdActions: cloneDeep(this.checklist.ComplianceThresholdActions),
                    accessRightFilterCollection: this.availableAccessRightFilterCollection,
                    checkpointName: undefined,
                    checklistName: this.checklist.Name,
                },
                headerText: this.appTextService.get('checklist.compliance_threshold'),
                isFooterDividerShown: false,
                config: {
                    panelClass: [
                        'fi-dialog-with-dividers',
                        'fi-compliance-threshold-panel',
                        'fi-rounded-dialog-panel',
                        'fi-rounded-dialog-full-round-buttons',
                    ],
                    autoFocus: false,
                    width: _commonDialogOptions.config.width,
                    closeOnNavigation: false,
                },
            } as IFiDialogOptions<IComplianceThresholdDialogData, IComplianceThresholdDialogResult>);

        this.fiDialogsService
            .openAnimatedBottomDialog(dialogOptions)
            .onYesButtonClick((result) => {
                this.checklist.IsComplianceThresholdEnabled = result.isComplianceThresholdEnabled;
                this.checklist.ComplianceThreshold = result.complianceThreshold;
                this.checklist.ComplianceThresholdActions = result.complianceThresholdActions;
                this.cdr.markForCheck();
            })
            .afterClosed(() => {
                this.setSettingsTooltipHiddenStatus(false);
                this.saveChangesWarningService.closeDialogOnTransition = false;
            });

        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    private isSettingsDialogOpened: boolean = false;

    private openSettingsDialog(): void {
        this.isSettingsDialogOpened = true;
        this.fiItemSharingSettingsDialogService
            .openItemSharingSettingsDialog(
                {
                    isShared: this.checklist.IsShared,
                    showSharingSelection: true,
                    businessUnitIdsOutOfScope: this.businessUnitIdsOutOfScope,
                    isItemOwner: this.checklist.CreatedByUserId === this.fiUserDataService.userData.UserId,
                    savedQuickSelectionFilters: this.savedQuickSelectionFilters,
                    selectedFiltersCollection: this.selectedFiltersCollection,
                    availableAccessRightFilterCollection: this.availableAccessRightFilterCollection,
                    itemAccessRightCoreFilter: cloneDeep(
                        this.checklist.AccessRightFilterCollection.AccessRightCoreFilter,
                    ),
                    itemAccessRightUserFilter: cloneDeep(
                        this.checklist.AccessRightFilterCollection.AccessRightUserFilter,
                    ),
                    dialogDescriptionText: this.appTextService.get('checklist.sharing_selection_description'),
                    individualUnitSectionDescriptionText: this.appTextService.get(
                        'checklist.individual_units_description',
                    ),
                    moduleType: this.auditMode ? ModuleType.ChecklistV2Audit : ModuleType.ChecklistV2,
                },
                this.appTextService.get('sharing_settings'),
            )
            .onYesButtonClick((result) => {
                this.checklist.AccessRightFilterCollection.AccessRightCoreFilter = result.accessRightCoreFilter;
                this.checklist.IsShared = result.isShared;
                this.cdr.markForCheck();
            })
            .afterClosed((result) => {
                this.savedQuickSelectionFilters = cloneDeep(result.value.savedQuickSelectionFilters);
                this.setSelectedFiltersCollection();
                this.saveChangesWarningService.closeDialogOnTransition = false;
                this.isSettingsDialogOpened = false;
            });

        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    private openQRCodesDialog(): void {
        this.setSettingsTooltipHiddenStatus(true);

        const _commonDialogOptions = cloneDeep(this.commonDialogOptions);
        const dialogOptions: IFiDialogOptions<IFiChecklist2QRCodesDialogData, IFiChecklist2QRCodesDialogResult> =
            Object.assign(_commonDialogOptions, {
                contentComponentRef: FiChecklist2QRCodesDialogComponent,
                contentComponentData: {
                    isQRCodeEnabled: this.checklist.IsQRCodeEnabled,
                    checkpointGroups: this.checklist.CheckpointGroups,
                },
                headerText: this.appTextService.get('qr_common.qr_codes'),
                isFooterDividerShown: false,
                headerDividerClass: 'BrandedBorderColor',
                config: {
                    panelClass: [
                        'fi-new-scrollbar-content-dialog-panel',
                        'fi-rounded-dialog-panel',
                        'fi-rounded-dialog-full-round-buttons',
                    ],
                    autoFocus: false,
                    closeOnNavigation: false,
                },
            });

        this.fiDialogsService
            .custom(dialogOptions)
            .onYesButtonClick((qrCodesDialogResult) => {
                this.checklist.IsQRCodeEnabled = qrCodesDialogResult.newIsQRCodeEnabled;
                this.cdr.markForCheck();
            })
            .afterClosed(() => {
                this.setSettingsTooltipHiddenStatus(false);
                this.saveChangesWarningService.closeDialogOnTransition = false;
            });

        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    protected addCheckpointsFromAI(aiChecklist2: IAIChecklist2Model): void {
        this.fiChecklistHelperService.mergeCheckpointsWithAIResponse(
            this.checklist.CheckpointGroups,
            aiChecklist2,
            this.selectedLanguageId,
        );
        this.navigateToCheckpointSections.emit();
    }

    protected onAIAssistanceDialogOpenChange(isOpened: boolean): void {
        this.setSettingsTooltipHiddenStatus(isOpened);
    }

    protected getCheckpointMessage(): string {
        return this.appTextService.get(
            this.templateMode ? 'checklist.template_checkpoints_message' : 'checklist.checkpoints_message',
        );
    }

    protected getComplianceThresholdMessage(): string {
        if (!this.checklist.IsComplianceThresholdEnabled) {
            return this.appTextService.get('checklist.has_no_compliance_threshold');
        }
        return this.appTextService.get('checklist.compliance_threshold_set_at', {
            '{0}': this.checklist.ComplianceThreshold,
        });
    }

    protected getCheckpointsAmount(): number {
        return this.fiChecklistHelperService.getCheckpointsAmount(this.checklist.CheckpointGroups);
    }

    protected getCheckpointsAmountLabel(): string {
        const checkpointsAmount = this.fiChecklistHelperService.getCheckpointsAmount(this.checklist.CheckpointGroups);
        return `${checkpointsAmount} ${this.appTextService
            .get(
                this.isSingleSharingOption(checkpointsAmount)
                    ? 'checklist.names_of_checkpoints'
                    : 'checklist.checkpoints',
            )
            .toLowerCase()}`;
    }

    protected getQRCodesLabel(): string {
        if (!this.checklist.IsQRCodeEnabled) return this.appTextService.get('qr_common.qr_code_disabled');

        const checkpointsCount = this.checklist.CheckpointGroups.reduce(
            (curr, checkpointGroup) => curr + checkpointGroup.Checkpoints.length,
            0,
        );
        const countText =
            checkpointsCount +
            ' ' +
            this.appTextService.get(checkpointsCount !== 1 ? 'qr_common.qr_codes' : 'qr_common.qr_code');
        return this.appTextService.get('qr_common.qr_codes_n_available', {
            '{0}': countText,
        });
    }

    protected getAreasAmountLabel(): string {
        const sharingNoMainAreasLength: number = this.getSelectedNoMainAreasLength();
        return `${sharingNoMainAreasLength} ${this.appTextService
            .get(sharingNoMainAreasLength === 1 ? 'common.area' : 'common.areas')
            .toLowerCase()}`;
    }

    private getSelectedNoMainAreasLength(): number {
        const outOfScopeAreas: ILightWeightArea[] = this.fiAccessRightHelperService.getOutOfScopeItems(
            this.availableAccessRightFilterCollection.AccessRightCoreFilter.Areas,
            this.checklist.AccessRightFilterCollection.AccessRightCoreFilter.Areas,
        ) as ILightWeightArea[];
        const accessRightAvailableAreas: ILightWeightArea[] =
            this.availableAccessRightFilterCollection.AccessRightCoreFilter.Areas;
        const allAreas: ILightWeightArea[] = [
            ...fiItemsFlatten(accessRightAvailableAreas),
            ...fiItemsFlatten(outOfScopeAreas),
        ];
        return this.checklist.AccessRightFilterCollection.AccessRightCoreFilter.AreaIds.filter(
            (selectedAreaId: number) => {
                return !!allAreas.find((area: ILightWeightArea) => {
                    return selectedAreaId === area.Id && !area.ChildItems?.length;
                });
            },
        )?.length;
    }

    protected getSharedAreasAmount(): number {
        return this.checklist.AccessRightFilterCollection.AccessRightCoreFilter.AreaIds.length;
    }

    protected getSharedUnitsAmountLabel(): string {
        const sharedBusinessUnitsAmount = this.getSharedUnitsAmount();

        return `${sharedBusinessUnitsAmount} ${this.appTextService
            .get(this.isSingleSharingOption(sharedBusinessUnitsAmount) ? 'common.unit' : 'common.units')
            .toLowerCase()}`;
    }

    protected getSharedUnitsAmount(): number {
        return this.checklist.AccessRightFilterCollection.AccessRightCoreFilter.BusinessUnitIds.length;
    }

    protected isCheckpointsCard(contentCardId: FiChecklistSectionTypes): boolean {
        return contentCardId === FiChecklistSectionTypes.Checkpoints;
    }

    protected isComplianceThresholdCard(contentCardId: FiChecklistSectionTypes): boolean {
        return contentCardId === FiChecklistSectionTypes.ComplianceThreshold;
    }

    protected isSettingsCard(contentCardId: FiChecklistSectionTypes): boolean {
        return contentCardId === FiChecklistSectionTypes.Settings;
    }

    protected isQRCodeCard(contentCardId: FiChecklistSectionTypes): boolean {
        return contentCardId === FiChecklistSectionTypes.QRCodes;
    }

    protected isSaveButtonDisabled(): boolean {
        // todo should we disable save button? if yes probably isSaveDisabled should go via @Input
        return false;
    }

    private validateCheckpointsFn(checkpointGroups: ICheckpointGroup[]): ValidationErrors {
        return {
            checkpointsRequired: !!checkpointGroups.filter((checkpointGroup) => {
                return !checkpointGroup.Checkpoints.length;
            }).length,
        };
    }

    private validateSharingSettingsFn(
        sharingSettingsModel: IAccessRightCoreFilter,
        isChecklistShared: boolean,
    ): ValidationErrors {
        return this.fiAccessRightHelperService.validateAccessRightsCoreFilters(sharingSettingsModel, {
            isShared: isChecklistShared,
            skipRolesCheck: true,
        });
    }

    protected validateSectionFn(sectionId: FiChecklistSectionTypes): ValidationErrors {
        if (this.isCheckpointsCard(sectionId)) return this.validateCheckpointsFn(this.checklist.CheckpointGroups);
        if (this.isSettingsCard(sectionId))
            return this.validateSharingSettingsFn(
                this.checklist.AccessRightFilterCollection.AccessRightCoreFilter,
                this.checklist.IsShared,
            );
    }

    public doValidate(c: AbstractControl): ValidationErrors {
        const result: ValidationErrors = {
            nameRequired: !this.ngModel.Names.some((name: ContentLanguageLabelModel) => name.Value),
        };
        return result;
    }

    protected getChecklistEditorData(): IFiObjectEditorInfo {
        return {
            ChangedByProfile: this.checklist.ChangedByProfile,
            CreatedByProfile: this.checklist.CreatedByProfile,
            ChangedOn: this.checklist.ChangedOn,
            CreatedOn: this.checklist.CreatedOn,
        };
    }

    protected isSectionTooltipShown(sectionId: FiChecklistSectionTypes): boolean {
        return (
            this.isSettingsCard(sectionId) &&
            this.isTemplateBased &&
            !this.isSettingsDialogOpened &&
            !this.settingsTooltipHidden
        );
    }

    protected getSharingSelectionTooltipText(): string {
        return this.appTextService.get(
            this.isTemplateBased && this.templateMode
                ? 'checklist.sharing_settings_template_reminder_tooltip_text'
                : 'checklist.sharing_settings_reminder_tooltip_text',
        );
    }

    private settingsTooltipHidden: boolean = false;

    private setSettingsTooltipHiddenStatus(value: boolean): void {
        this.settingsTooltipHidden = value;
        this.cdr.markForCheck();
    }

    private isSingleSharingOption(sharingOptionsAmount: number): boolean {
        return sharingOptionsAmount === 1;
    }

    private ngOnDestroy$ = new Subject<void>();

    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    private _checklistEditorForm: FiValidatingFormDirective;
    @ViewChild(FiValidatingFormDirective) set checklistEditorForm(value: FiValidatingFormDirective) {
        if (isDefined(value)) this._checklistEditorForm = value;
    }

    protected submitChecklistForm(): void {
        this._checklistEditorForm.submit();
    }

    @Output() onSave = new EventEmitter<void>();

    protected save(): void {
        this.onSave.emit();
    }

    protected onValidateError(): void {
        this.fiDialogsService.notify({
            headerText: this.appTextService.get('common.warning'),
            messageText: this.appTextService.get('common.error_all_mandatory'),
            config: {
                panelClass: ['fi-rounded-dialog-panel'],
                autoFocus: false,
                restoreFocus: false,
            },
        });
    }

    protected readonly FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;
}
